import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from "axios";
import { API } from '../../config/api';
import { Util } from '../../util/util';
const initialState = {
    userObject: {},
    googleConfig: null,
    googleAuthLoading: false,
    facebookConfig: null,
    facebookAuthLoading: false,
    recaptchaConfig: null
}

export const doLogin = createAsyncThunk("login/auth",
 async (req) => {
    // Send login request with recaptchaToken in request body
    const response = await axios.post(API.login, {
        email: req.email,
        password: req.password,
        recaptchaToken: req.recaptchaToken
    });
    return response?.data;
});

export const fetchGoogleConfig = createAsyncThunk("login/googleConfig",
 async () => {
    const response = await axios.get(API.googleConfig);
    return response?.data;
});

export const doGoogleLogin = createAsyncThunk("login/googleAuth",
 async (credential) => {
    const response = await axios.post(API.oAuth, { oauth_token: credential, provider: "google" });
    return response?.data;
});

export const fetchFacebookConfig = createAsyncThunk("login/facebookConfig",
 async () => {
    const response = await axios.get(API.facebookConfig, { silentError: true });
    return response?.data;
});

export const doFacebookLogin = createAsyncThunk("login/facebookAuth",
 async (accessToken) => {
    const response = await axios.post(API.oAuth, { oauth_token: accessToken, provider: "facebook" });
    return response?.data;
});

export const fetchRecaptchaConfig = createAsyncThunk("login/recaptchaConfig",
 async () => {
    const response = await axios.get(API.recaptchaConfig, { silentError: true });
    return response?.data;
});

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    clearSession : (state) => {
        state.userObject = {};
      }
  },
  extraReducers(builder) {
        builder
            .addCase(doLogin.fulfilled, (state, action) => {
                if(action?.payload?.data){
                    state.userObject = action.payload.data;
                    localStorage.setItem('userObject', JSON.stringify(action.payload.data));
                }else{
                    Util.showAlert(Util.alert_types.error, 'ERROR', 'Technical Error!' );
                }
            })
            .addCase(fetchGoogleConfig.fulfilled, (state, action) => {
                const config = action?.payload?.data || action?.payload;
                if(config?.clientId){
                    state.googleConfig = config;
                }
            })
            .addCase(fetchGoogleConfig.rejected, (state, action) => {
                console.error('Failed to fetch Google config:', action.error);
            })
            .addCase(doGoogleLogin.pending, (state) => {
                state.googleAuthLoading = true;
            })
            .addCase(doGoogleLogin.fulfilled, (state, action) => {
                state.googleAuthLoading = false;
                if(action?.payload?.data){
                    state.userObject = action.payload.data;
                    localStorage.setItem('userObject', JSON.stringify(action.payload.data));
                    Util.showAlert(Util.alert_types.success, 'SUCCESS!', 'Signed in with Google!' );
                }else{
                    Util.showAlert(Util.alert_types.error, 'ERROR', 'Google sign-in failed!' );
                }
            })
            .addCase(doGoogleLogin.rejected, (state, action) => {
                state.googleAuthLoading = false;
                console.error('Google sign-in failed:', action.error);
                Util.showAlert(Util.alert_types.error, 'ERROR', 'Google sign-in failed!' );
            })
            .addCase(fetchFacebookConfig.fulfilled, (state, action) => {
                const config = action?.payload?.data || action?.payload;
                if(config?.appId){
                    state.facebookConfig = config;
                }
            })
            .addCase(fetchFacebookConfig.rejected, (state, action) => {
                console.error('Failed to fetch Facebook config:', action.error);
            })
            .addCase(doFacebookLogin.pending, (state) => {
                state.facebookAuthLoading = true;
            })
            .addCase(doFacebookLogin.fulfilled, (state, action) => {
                state.facebookAuthLoading = false;
                if(action?.payload?.data){
                    state.userObject = action.payload.data;
                    localStorage.setItem('userObject', JSON.stringify(action.payload.data));
                    Util.showAlert(Util.alert_types.success, 'SUCCESS!', 'Signed in with Facebook!' );
                }else{
                    Util.showAlert(Util.alert_types.error, 'ERROR', 'Facebook sign-in failed!' );
                }
            })
            .addCase(doFacebookLogin.rejected, (state, action) => {
                state.facebookAuthLoading = false;
                console.error('Facebook sign-in failed:', action.error);
                Util.showAlert(Util.alert_types.error, 'ERROR', 'Facebook sign-in failed!' );
            })
            .addCase(fetchRecaptchaConfig.fulfilled, (state, action) => {
                const config = action?.payload?.data || action?.payload;
                if(config?.siteKey){
                    state.recaptchaConfig = config;
                    // Load reCAPTCHA script dynamically
                    if (window.loadRecaptcha) {
                        window.loadRecaptcha(config.siteKey);
                    }
                }
            })
            .addCase(fetchRecaptchaConfig.rejected, (state, action) => {
                console.error('Failed to fetch reCAPTCHA config:', action.error);
            })
    }
});

export const selectUser = (state) => state.login.userObject;
export const selectGoogleConfig = (state) => state.login.googleConfig;
export const selectGoogleAuthLoading = (state) => state.login.googleAuthLoading;
export const selectFacebookConfig = (state) => state.login.facebookConfig;
export const selectFacebookAuthLoading = (state) => state.login.facebookAuthLoading;
export const selectRecaptchaConfig = (state) => state.login.recaptchaConfig;
export const {clearSession  } = loginSlice.actions;


export default loginSlice.reducer;